import React from 'react';
import styled from 'styled-components/macro';
import { Col, Popover } from 'antd';
import { ClockCircleOutlined, ConsoleSqlOutlined, TableOutlined, TeamOutlined, HddOutlined } from '@ant-design/icons';
import { formatNumberWithoutAbbreviation } from '../../../shared/formatNumber';
import { ANTD_GRAY } from '../../shared/constants';
import { toLocalDateTimeString, toRelativeTimeString } from '../../../shared/time/timeUtils';
import { StatsSummary } from '../../shared/components/styled/StatsSummary';
import { FormattedBytesStat } from './FormattedBytesStat';
import { countFormatter, needsFormatting } from '../../../../utils/formatter';
import { useTranslation } from '../../../../i18n/useTranslation';
import ExpandingStat from './ExpandingStat';

const StatText = styled.span<{ color: string }>`
    color: ${(props) => props.color};
`;

const PopoverContent = styled.div`
    max-width: 300px;
`;

type Props = {
    rowCount?: number | null;
    columnCount?: number | null;
    sizeInBytes?: number | null;
    totalSqlQueries?: number | null;
    queryCountLast30Days?: number | null;
    uniqueUserCountLast30Days?: number | null;
    lastUpdatedMs?: number | null;
    color?: string;
    mode?: 'normal' | 'tooltip-content';
};

export const DatasetStatsSummary = ({
    rowCount,
    columnCount,
    sizeInBytes,
    totalSqlQueries,
    queryCountLast30Days,
    uniqueUserCountLast30Days,
    lastUpdatedMs,
    color,
    mode = 'normal',
}: Props) => {
    const { t } = useTranslation();
    const isTooltipMode = mode === 'tooltip-content';
    const displayedColor = isTooltipMode ? '' : color ?? ANTD_GRAY[7];

    const statsViews = [
        !!rowCount && (
            <ExpandingStat
                disabled={isTooltipMode || !needsFormatting(rowCount)}
                render={(isExpanded) => (
                    <StatText className="f-text-content f-color-dark-black-s70" color={displayedColor}>
                        <TableOutlined style={{ marginRight: 8, color: displayedColor }} />
                        <b>{isExpanded ? formatNumberWithoutAbbreviation(rowCount) : countFormatter(rowCount)}</b>{' '}
                        {t('rows')}
                        {!!columnCount && (
                            <>
                                ,{' '}
                                <b>
                                    {isExpanded
                                        ? formatNumberWithoutAbbreviation(columnCount)
                                        : countFormatter(columnCount)}
                                </b>{' '}
                                {t('columns')}
                            </>
                        )}
                    </StatText>
                )}
            />
        ),
        !!sizeInBytes && (
            <StatText className="f-text-content f-color-dark-black-s70" color={displayedColor}>
                <HddOutlined style={{ marginRight: 8, color: displayedColor }} />
                <FormattedBytesStat bytes={sizeInBytes} />
            </StatText>
        ),
        (!!queryCountLast30Days || !!totalSqlQueries) && (
            <StatText className="f-text-content f-color-dark-black-s70" color={displayedColor}>
                <ConsoleSqlOutlined style={{ marginRight: 8, color: displayedColor }} />
                <b>{formatNumberWithoutAbbreviation(queryCountLast30Days || totalSqlQueries)}</b>{' '}
                {queryCountLast30Days ? <>{t('queries last month')}</> : <>{t('monthly queries')}</>}
            </StatText>
        ),
        !!uniqueUserCountLast30Days && (
            <StatText className="f-text-content f-color-dark-black-s70" color={displayedColor}>
                <TeamOutlined style={{ marginRight: 8, color: displayedColor }} />
                <b>{formatNumberWithoutAbbreviation(uniqueUserCountLast30Days)}</b> {t('unique users')}
            </StatText>
        ),
        !!lastUpdatedMs && (
            <Popover
                content={
                    <PopoverContent>
                        {t('Data was last updated in the source platform on')}
                        <strong>{toLocalDateTimeString(lastUpdatedMs)}</strong>
                    </PopoverContent>
                }
            >
                <StatText className="f-text-content f-color-dark-black-s70" color={displayedColor}>
                    <ClockCircleOutlined style={{ marginRight: 8, color: ANTD_GRAY[7] }} />
                    {t('Updated')} {toRelativeTimeString(lastUpdatedMs)}
                </StatText>
            </Popover>
        ),
    ].filter((stat) => stat);

    return (
        <>
            {statsViews.length > 0 && (
                <Col xs={24}>
                    <StatsSummary stats={statsViews} />
                </Col>
            )}
        </>
    );
};
